import * as preact from "preact";
import type { BookData } from "stores/BookStore";
import type { ApplyTranscriptMode } from "views/ApplyTranscriptDialog";
import * as applyTranscriptDialog from "views/ApplyTranscriptDialog";

const maxSRTFileBytes = 5 * 1024 * 1024;

interface Props {
  allowYouTube?: boolean;
  book: BookData;
  onRemoveYouTube?: () => void;
  onSaveYouTubeID?: (userInput: string) => void;
  onSubmitYouTubeTranscript?: (
    transcript: string,
    mode: ApplyTranscriptMode
  ) => void | Promise<void>;
}

interface State {
  submitting: boolean;
  transcriptText: string;
}

class YouTubeModal extends preact.Component<Props, State> {
  private unmounted = false;
  private transcriptInputRef = preact.createRef<HTMLTextAreaElement>();
  private srtFileInputRef = preact.createRef<HTMLInputElement>();
  private youTubeInputRef = preact.createRef<HTMLInputElement>();

  constructor(props: Props) {
    super(props);
    this.state = { submitting: false, transcriptText: "" };
  }

  componentWillUnmount() {
    this.unmounted = true;
  }

  private handleTranscriptInput = (event: Event) => {
    this.setState({
      transcriptText: (event.target as HTMLTextAreaElement).value,
    });
  };

  /** Both transcript sources ask how to apply the timings before submitting. */
  private applyTranscript = (transcript: string, filename?: string) => {
    if (transcript.trim().length === 0 || this.state.submitting) {
      return;
    }
    applyTranscriptDialog.show({ filename }, async (mode) => {
      this.setState({ submitting: true });
      try {
        await this.props.onSubmitYouTubeTranscript?.(transcript, mode);
      } catch (error) {
        // readlangClient already tells the user about network/server failures.
        console.error(error);
      } finally {
        if (!this.unmounted) {
          this.setState({ submitting: false });
        }
      }
    });
  };

  private handleSubmitClick = () => {
    this.applyTranscript(this.state.transcriptText);
  };

  private handleSRTFileChange = (event: Event) => {
    const fileInput = event.currentTarget as HTMLInputElement;
    const file = fileInput.files?.[0];
    fileInput.value = "";
    if (!file) {
      return;
    }
    if (file.size > maxSRTFileBytes) {
      alert("Subtitle file is too large (max 5MB).");
      return;
    }

    const reader = new FileReader();
    reader.onload = () =>
      this.applyTranscript(String(reader.result ?? ""), file.name);
    reader.onerror = () => alert("Failed to read subtitle file.");
    reader.readAsText(file);
  };

  render() {
    const { allowYouTube, book, onRemoveYouTube, onSaveYouTubeID } = this.props;
    const { submitting } = this.state;
    const hasTranscript = this.state.transcriptText.trim().length > 0;

    if (!allowYouTube) {
      return (
        <div class="youTubeModal">
          <p>
            You can only add YouTube videos to documents with 20,000 words or
            fewer.
          </p>
        </div>
      );
    }

    return (
      <div class="youTubeModal" aria-busy={submitting}>
        <h3>Synchronized YouTube video</h3>
        <p>
          {!book.youTubeID && (
            <>
              Paste YouTube Video URL here:{" "}
              <input
                type="text"
                id="youTubeID"
                defaultValue={book.youTubeID || ""}
                ref={this.youTubeInputRef}
                style={{ marginBottom: "3px" }}
              />
              <button
                id="saveYouTubeID"
                onClick={() =>
                  onSaveYouTubeID?.(this.youTubeInputRef.current?.value ?? "")
                }
              >
                <i class="icon-plus"></i> Add YouTube Video
              </button>
            </>
          )}
          {book.youTubeID && (
            <>
              <a href={`https://www.youtube.com/watch?v=${book.youTubeID}`}>
                Link to YouTube video
              </a>{" "}
              &nbsp; &nbsp;{" "}
              <button id="removeYouTube" onClick={onRemoveYouTube}>
                <i class="icon-remove"></i> Remove This Video
              </button>
            </>
          )}
        </p>

        {book.youTubeID && (
          <>
            <h3>Paste transcript timings from YouTube (BETA)</h3>
            <p>
              If there is a "Show Transcript" button in the description on the{" "}
              <a
                class="youTubeTabLink"
                href={`https://www.youtube.com/watch?v=${book.youTubeID}`}
                target="_blank"
              >
                YouTube page for this video
              </a>
              , you can click it and copy and paste the transcript with the
              timings to speed up or avoid doing the manual synchronization
              yourself.
            </p>
            <p>
              Alternatively, you can paste the contents of an{" "}
              <strong>.SRT</strong> subtitle file (e.g. one generated by Whisper
              or other tools), or upload an .SRT file directly using the button
              below.
            </p>
            <textarea
              placeholder="Paste the YouTube transcript or .SRT contents here"
              rows={5}
              id="youTubeTranscriptWithTimings"
              ref={this.transcriptInputRef}
              onInput={this.handleTranscriptInput}
            ></textarea>
            <input
              type="file"
              id="srtFileUpload"
              accept=".srt,.vtt,text/plain"
              ref={this.srtFileInputRef}
              onChange={this.handleSRTFileChange}
            />
            {/* The .SRT entry point also submits, so the busy button shows
                  regardless of which one started the upload */}
            {hasTranscript || submitting ? (
              <button
                id="submitYouTubeTranscript"
                disabled={submitting}
                onClick={this.handleSubmitClick}
              >
                {submitting ? (
                  <>
                    <i class="icon-spinner icon-spin" aria-hidden="true"></i>{" "}
                    Uploading transcript and updating the text…
                  </>
                ) : (
                  "Submit transcript"
                )}
              </button>
            ) : (
              <button
                id="srtFileUploadButton"
                onClick={(event) => {
                  event.preventDefault();
                  this.srtFileInputRef.current?.click();
                }}
              >
                <i class="icon-upload"></i> Load .SRT file…
              </button>
            )}

            <h3>Manual synchronization</h3>
            <h4>To synchronize the video</h4>
            <ul>
              <li>Click the "Sync" button below the video</li>
              <li>
                As the video plays, click on a word exactly when you hear it to
                create a sync point
              </li>
            </ul>
            <h4>To play the video</h4>
            <ul>
              <li>Click the "Play" button below the video</li>
              <li>Click on any word and the video will play from that point</li>
            </ul>
            <h4>To translate</h4>
            <ul>
              <li>Ensure the video is synchronized (see above)</li>
              <li>Click the "Translate" button below the video</li>
              <li>Click on any word to translate it, as usual</li>
              <li>
                Later, when reviewing your words, you will be able to play back
                the video corresponding to the word's context
              </li>
            </ul>
            <h4>Walkthrough tutorial</h4>
            <p>
              Here's a YouTube video demonstrating the manual synchronization
              process:{" "}
              <a href="https://www.youtube.com/watch?v=szcvArpfxWI">
                Synchronizing YouTube videos
              </a>
            </p>
          </>
        )}
      </div>
    );
  }
}

export default YouTubeModal;
